/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, {
  DEFAULT_FONT_SIZE,
  type DefaultIcon,
} from './WireframeBase';
import tools from '../tools';
import wireframeProgressStepper from '../assets/icons/wireframeProgressStepper.svg';
import wireframeStyleBreadcrumbSlash from '../assets/icons/wireframeStyleBreadcrumbSlash.svg';
import wireframeStyleBreadcrumbAngle from '../assets/icons/wireframeStyleBreadcrumbAngle.svg';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { Text } from '@lib/canvasx/components/Text';
import { SPACER_3, SPACER_4, SPACER_6 } from '../utils/style';
import {
  WIREFRAME_PRIMARY_COLOR,
  WIREFRAME_SELECTION_COLOR,
} from '../utils/wireframes';
import { SvgIcon } from '../components/wireframes/SvgIcon';
import WireframeAddItemControl from '../controls/WireframeAddItemControl';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import {
  ProgressStep,
  type ProgressStepProps,
} from '../components/wireframes/ProgressStep';
import { InputField } from '../components/wireframes/InputField';
import { FlexColumn } from '@lib/canvasx/components/FlexColumn';

// Ensure progress stepper height remains the same regardless of style options
// selected. This is a simple solution for now as we don't allow for font size
// changes
const STEP_HEIGHT = 45;
const NUMBER_PADDING_VERTICAL = 6;
const NUMBER_PADDING_HORIZONTAL = NUMBER_PADDING_VERTICAL * 1.5;
class WireframeProgressStepper extends WireframeBase {
  hasDraggableItems = true;

  initItems() {
    if (this.fabricObject.items.items.length === 0) {
      this.fabricObject.items.add({
        value: 'First step',
        iconLeft: {
          prefix: 'fas',
          iconName: 'code',
        },
      });
      this.fabricObject.items.add({
        value: 'Second step',
        iconLeft: {
          prefix: 'fas',
          iconName: 'magnifying-glass',
        },
      });
      this.fabricObject.items.add({
        value: 'Third step',
        iconLeft: {
          prefix: 'fas',
          iconName: 'star',
        },
      });
    }
  }

  tools() {
    if (this.activeKey) {
      const itemTools = [[tools.ToolWireframeIcon]];

      // Show delete tool only if more than two items left
      if (this.fabricObject.items.items.length > 2) {
        itemTools.push([tools.ToolWireframeDeleteItem]);
      }

      return itemTools;
    }
    return [
      [tools.ToolWireframeAddItem],
      [tools.ToolProgressStepperStyleFlags],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
      [tools.ToolWireframeTextColor],
    ];
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls() {
    const controls = super.createDefaultControls();
    return {
      ...controls,
      addItem: new WireframeAddItemControl({ layout: 'row' }),
    };
  }

  static defaultWidth(): number {
    return 255;
  }

  static defaultHeight(): number {
    return 40;
  }

  static iconSvg() {
    return wireframeProgressStepper;
  }

  static externalName(): string {
    return 'Progress stepper';
  }

  static componentStyles() {
    return {
      slash: wireframeStyleBreadcrumbSlash,
      angle: wireframeStyleBreadcrumbAngle,
    };
  }

  componentStyles() {
    return WireframeProgressStepper.componentStyles();
  }

  defaultText() {
    return 'Step';
  }

  defaultIcon(): DefaultIcon {
    return { iconLeft: 'star' };
  }

  supportedIcons(): string[] {
    return ['iconLeft'];
  }

  setIcon(location: any, icon: any): void {
    const item = this.activeItem();
    // If this item has no text and the icon is removed, delete the item
    if (item && icon === null && !item.value) {
      this.deleteItem();
      return;
    }

    super.setIcon(location, icon);
  }

  styleFlagEnabled(key) {
    // Avoid empty step by ensuring at least one style flag is enabled at all times
    return ['showIcons', 'showStepText', 'showStepNumbers'].some(
      flag => this.fabricObject[flag] && key !== flag
    );
  }

  stepType(index: number): ProgressStepProps['type'] {
    if (index === 0) {
      return 'start';
    }
    if (index === this.fabricObject.items.items.length - 1) {
      return 'end';
    }
    return 'middle';
  }

  get slashSeparator() {
    return this.fabricObject.style === 'slash';
  }

  get fontProps() {
    const { fontSize = DEFAULT_FONT_SIZE, textColor } = this.fabricObject;
    return {
      size: fontSize,
      color: textColor,
    };
  }

  wasSet(keyOrPath: KeyPath, value: any): void {
    if (keyOrPath === 'textEditing' && !value) {
      // Remove empty items when user finishes editing
      this.fabricObject.items.items.forEach(item => {
        if (!item.value && !item.iconLeft) {
          this.deleteItem(item.id);
        }
      });
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const {
      items,
      fontSize = DEFAULT_FONT_SIZE,
      textColor,
      showIcons,
      showStepText,
      showStepNumbers,
    } = this.fabricObject;
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;
    const iconSize = fontSize + 2;

    let renderedDimensions;

    const maxItemDimensions = this.maxItemDimensions;

    this.resetMaxItemDimensions();

    ctx.save();

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: 1,
      },
      () => {
        renderElement(
          <FlexRow
            verticalAlignment='center'
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
          >
            {items.items.map((item, i) => {
              const active = this.activeKey.includes(item.id);
              const number = i + 1;
              const itemWidth = maxItemDimensions?.width ?? undefined;
              const stepType = this.stepType(i);

              const padding =
                stepType === 'start'
                  ? { horizontal: SPACER_4 }
                  : { horizontal: SPACER_6 };
              return (
                <FlexRow
                  key={item.id}
                  border={active ? WIREFRAME_SELECTION_COLOR : 'transparent'}
                  verticalAlignment='center'
                  setRenderedDimensions={dimensions => {
                    item.containerDimensions = dimensions;
                  }}
                >
                  <ProgressStep type={this.stepType(i)} fill={fill}>
                    <FlexColumn width={itemWidth} horizontalAlignment='center'>
                      <FlexRow
                        minHeight={STEP_HEIGHT}
                        maxHeight={STEP_HEIGHT}
                        verticalAlignment='center'
                        padding={padding}
                        gap={SPACER_3}
                        wrap={false}
                        setRenderedDimensions={dimensions => {
                          this.updateMaxItemDimensions({
                            dimensions,
                            useUnwrappedWidth: true,
                          });
                          item.dimensions = dimensions;
                        }}
                      >
                        {showStepNumbers && (
                          <FlexRow
                            padding={{
                              horizontal: NUMBER_PADDING_HORIZONTAL,
                              vertical: NUMBER_PADDING_VERTICAL,
                            }}
                            fill={{ color: 'white', opacity: 0.2 }}
                            borderRadius={64}
                          >
                            <Text {...this.fontProps}>{number}</Text>
                          </FlexRow>
                        )}
                        {showIcons && (
                          <SvgIcon
                            iconData={item.iconLeft}
                            size={iconSize}
                            iconFill={textColor}
                          />
                        )}
                        {showStepText && (
                          <InputField
                            wireframe={this}
                            item={item}
                            textKey={`text:${item.id}`}
                            showIcons={false}
                            {...this.fontProps}
                            onChange={value => {
                              this.onItemChange(value);
                            }}
                          />
                        )}
                      </FlexRow>
                    </FlexColumn>
                  </ProgressStep>
                </FlexRow>
              );
            })}
          </FlexRow>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );

    ctx.restore();
  }
}

export default WireframeProgressStepper;
